import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { readWithTimeout } from '../lib/supabaseRead';

// A reopened note waits for the previous instance's pending save. Shared only
// within this browser runtime, keyed by exact account and project.
const pendingWrites = new Map();

export function useDANote(userId, daId, enabled) {
  const [state, setState] = useState({ key: '', text: '', loaded: false, saved: false, error: '' });
  const scopeRef = useRef(null);
  const key = JSON.stringify([userId, daId, enabled]);

  useEffect(() => {
    const scope = { key, identity: JSON.stringify([userId, daId]), userId, daId, active: true, loaded: false, dirty: false, text: '', revision: 0, timer: null, queue: Promise.resolve() };
    scopeRef.current = scope;
    setState({ key, text: '', loaded: false, saved: false, error: '' });
    if (enabled && userId && daId) {
      (pendingWrites.get(scope.identity) || Promise.resolve()).then(() => readWithTimeout(signal => supabase.from('da_notes').select('note_text').eq('user_id', userId).eq('da_id', daId).maybeSingle().abortSignal(signal), { label: 'da_note_load' }))
        .then(({ data, error }) => {
          if (!scope.active) return;
          if (error) throw error;
          scope.loaded = true;
          scope.text = data?.note_text || '';
          setState({ key, text: scope.text, loaded: true, saved: false, error: '' });
        }).catch(() => {
          if (scope.active) setState({ key, text: '', loaded: false, saved: false, error: 'Could not load your note. Reopen this project to retry.' });
        });
    }
    return () => {
      clearTimeout(scope.timer);
      // Flush an edit to its original account/project even if navigation occurs before debounce.
      persist(scope);
      scope.active = false;
    };
  }, [key, userId, daId, enabled]);

  function persist(scope) {
    if (!scope?.loaded || !scope.dirty) return;
    clearTimeout(scope.timer);
    const value = scope.text;
    const revision = scope.revision;
    scope.dirty = false;
    const write = (pendingWrites.get(scope.identity) || Promise.resolve()).then(async () => {
      try {
        const { error } = value.trim() === ''
          ? await supabase.from('da_notes').delete().eq('user_id', scope.userId).eq('da_id', scope.daId)
          : await supabase.from('da_notes').upsert({ user_id: scope.userId, da_id: scope.daId, note_text: value, updated_at: new Date().toISOString() }, { onConflict: 'user_id,da_id' });
        if (error) throw error;
        if (scope.active && scope.revision === revision) setState(s => ({ ...s, saved: true, error: '' }));
      } catch {
        if (scope.revision === revision) scope.dirty = true;
        if (scope.active && scope.revision === revision) setState(s => ({ ...s, saved: false, error: 'Note not saved. Keep this page open and retry.' }));
      }
    });
    pendingWrites.set(scope.identity, write);
    write.finally(() => { if (pendingWrites.get(scope.identity) === write) pendingWrites.delete(scope.identity); });
  }

  const current = state.key === key ? state : { text: '', loaded: false, saved: false, error: '' };
  return {
    ...current,
    handleChange(value) {
      const scope = scopeRef.current;
      if (!scope?.loaded || scope.key !== key) return;
      scope.text = value; scope.dirty = true; scope.revision++;
      setState(s => ({ ...s, text: value, saved: false, error: '' }));
      clearTimeout(scope.timer);
      scope.timer = setTimeout(() => persist(scope), 1000);
    },
    handleBlur() { if (scopeRef.current?.key === key) persist(scopeRef.current); },
  };
}
